import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { PropertyType } from "@/api.types.v2";

const API_URL = import.meta.env.VITE_API_URL;

// ========================= Types =========================

export interface PropertySurcharge {
  id: string;
  propertyType: PropertyType;
  surchargeCents: number;
  /** FK to CarrierService. Null = applies to all carrier services. */
  carrierServiceId?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePropertySurchargeInput {
  propertyType: PropertyType;
  surchargeCents: number;
  carrierServiceId?: string | null;
  active?: boolean;
}

export type UpdatePropertySurchargeInput =
  Partial<CreatePropertySurchargeInput>;

export interface BulkUpdatePropertySurchargesInput {
  surcharges: CreatePropertySurchargeInput[];
}

// ========================= API Hooks =========================

/**
 * Get all property surcharges
 */
export function useGetPropertySurcharges(params?: {
  active?: boolean;
  carrierServiceId?: string;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["property-surcharges", params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.active !== undefined)
        queryParams.append("active", String(params.active));
      if (params?.carrierServiceId)
        queryParams.append("carrierServiceId", params.carrierServiceId);

      const { data } = await axios.get<{ data: PropertySurcharge[] }>(
        `${API_URL}/api/v2/admin/pricing/property-surcharges${queryParams.toString() ? `?${queryParams.toString()}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

/**
 * Get property surcharge by ID
 */
export function useGetPropertySurchargeById(id: string) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["property-surcharge", id],
    queryFn: async () => {
      const { data } = await axios.get<{ data: PropertySurcharge }>(
        `${API_URL}/api/v2/admin/pricing/property-surcharges/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.data;
    },
    enabled: !!token && !!id,
  });
}

/**
 * Create or update property surcharge
 */
export function useUpsertPropertySurcharge(options?: {
  onSuccess?: (data: PropertySurcharge) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePropertySurchargeInput) => {
      const { data } = await axios.post<{ data: PropertySurcharge }>(
        `${API_URL}/api/v2/admin/pricing/property-surcharges`,
        input,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["property-surcharges"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Update property surcharge by ID
 */
export function useUpdatePropertySurcharge(options?: {
  onSuccess?: (data: PropertySurcharge) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: string;
      data: UpdatePropertySurchargeInput;
    }) => {
      const { data } = await axios.patch<{ data: PropertySurcharge }>(
        `${API_URL}/api/v2/admin/pricing/property-surcharges/${id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["property-surcharges"] });
      void queryClient.invalidateQueries({
        queryKey: ["property-surcharge", data.id],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Delete property surcharge
 */
export function useDeletePropertySurcharge(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(
        `${API_URL}/api/v2/admin/pricing/property-surcharges/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["property-surcharges"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

/**
 * Bulk update property surcharges
 */
export function useBulkUpdatePropertySurcharges(options?: {
  onSuccess?: (data: PropertySurcharge[]) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: BulkUpdatePropertySurchargesInput) => {
      const { data } = await axios.post<{ data: PropertySurcharge[] }>(
        `${API_URL}/api/v2/admin/pricing/property-surcharges/bulk`,
        input,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["property-surcharges"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Initialize default property surcharges
 */
export function useInitializeDefaultSurcharges(options?: {
  onSuccess?: (data: PropertySurcharge[]) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await axios.post<{ data: PropertySurcharge[] }>(
        `${API_URL}/api/v2/admin/pricing/property-surcharges/initialize`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["property-surcharges"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Format currency helper
 */
export function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Get property type display name
 */
export function getPropertyTypeLabel(propertyType: PropertyType): string {
  const labels: Record<PropertyType, string> = {
    [PropertyType.HOUSE]: "House",
    [PropertyType.CONDO_LOBBY]: "Condo (Lobby)",
    [PropertyType.CONDO_UNIT]: "Condo (Unit)",
    [PropertyType.OFFICE]: "Office",
    [PropertyType.WAREHOUSE]: "Warehouse",
    [PropertyType.RETAIL]: "Retail",
  };
  return labels[propertyType] || propertyType;
}
